<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import { ReservationsService } from '@/api/services/reservations.service';
  import { reportBadNumberInput } from '@/utils/form-validity.utils';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import V2ReservationDepositScopeField from '@/components/v2/reservations/settings/V2ReservationDepositScopeField.vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { reservationDepositScopePatch } from '@/domain/reservations/reservation-deposit-scope';
  import {
    getReservationTimeZone,
    toDateKey,
  } from '@/domain/reservations/reservation-time';
  import { findOverlappingShiftPair } from '@/domain/reservations/shift-overlaps';
  import { AlertType } from '@/interfaces/IAlert';
  import {
    isReservationSlotInterval,
    RESERVATION_SLOT_INTERVALS,
  } from '@/interfaces/IReservationSettings';
  import type {
    IReservationDateOverride,
    IReservationSettingsConfiguration,
    TReservationSlotInterval,
  } from '@/interfaces/IReservationSettings';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  const props = defineProps<{
    configuration: IReservationSettingsConfiguration;
    override?: IReservationDateOverride;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const modals = useModal();
  const ui = useUi();
  const store = useRestaurantStore();
  const { restaurantId, branchId } = props.configuration.settings;
  const modalName = 'v2-reservation-date-override';
  const zone = getReservationTimeZone(
    store.selectedBranch?.location,
    store.restaurant?.country
  );
  const date = ref(props.override?.date ?? toDateKey(new Date(), zone));
  const mode = ref(
    props.override?.closed
      ? 'closed'
      : props.override?.windows?.length
      ? 'custom'
      : 'open'
  );
  const onlineClosed = ref(props.override?.onlineClosed ?? false);
  const depositRequired = ref<boolean | null>(
    props.override?.depositRequired ?? null
  );
  const note = ref(props.override?.note ?? '');
  let windowId = 0;
  const windows = ref(
    (
      props.override?.windows ?? [
        { from: '12:00', to: '15:00', maxCovers: null },
      ]
    ).map((window) => ({
      id: ++windowId,
      from: window.from.slice(0, 5),
      to: window.to.slice(0, 5),
      maxCovers: window.maxCovers?.toString() ?? '',
      // Not editable here yet; kept so a re-save does not reset the window to the grid.
      arrivalTimes: window.arrivalTimes ?? null,
    }))
  );
  /** Grid step of the custom windows; null = as the regular shifts. */
  const slotIntervalMin = ref<TReservationSlotInterval | null>(
    props.override?.slotIntervalMin ?? null
  );
  const intervals = RESERVATION_SLOT_INTERVALS;
  const saving = ref(false);
  const submitted = ref(false);
  const error = ref('');
  let alive = true;
  onBeforeUnmount(() => {
    alive = false;
  });
  function current() {
    return (
      alive &&
      store.restaurant?.id === restaurantId &&
      store.selectedBranch?.id === branchId
    );
  }
  watch(
    () => [store.restaurant?.id, store.selectedBranch?.id],
    () => {
      if (!current()) modals.hide(modalName);
    }
  );
  const tr = (key: string) => String(proxy.$t(key));
  function intervalChanged(event: Event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    const minutes = Number(event.target.value);
    slotIntervalMin.value = isReservationSlotInterval(minutes) ? minutes : null;
  }
  const modeItems = computed(() =>
    ['open', 'custom', 'closed'].map((id) => ({
      id,
      label: tr(`reservations.dates.form.mode.${id}`),
    }))
  );
  const dateValid = computed(() => {
    try {
      return (
        /^\d{4}-\d{2}-\d{2}$/.test(date.value) &&
        toDateKey(date.value, zone) === date.value
      );
    } catch {
      return false;
    }
  });
  const windowsValid = computed(() => {
    if (mode.value !== 'custom') return true;
    if (
      !windows.value.length ||
      windows.value.some(
        (window) =>
          !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(window.from) ||
          !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(window.to) ||
          window.from === window.to ||
          (window.maxCovers !== '' &&
            (!/^\d+$/.test(window.maxCovers) ||
              Number(window.maxCovers) < 1 ||
              Number(window.maxCovers) > 32767))
      )
    )
      return false;
    return (
      findOverlappingShiftPair(
        windows.value.map((window) => ({ ...window, days: [0] }))
      ) === null
    );
  });
  function setMode(value: string) {
    if (!saving.value) mode.value = value;
  }
  function addWindow() {
    windows.value.push({
      id: ++windowId,
      from: '19:00',
      to: '23:00',
      maxCovers: '',
      arrivalTimes: null,
    });
  }
  const form = ref<HTMLFormElement | null>(null);
  async function save() {
    submitted.value = true;
    if (reportBadNumberInput(form.value)) return;
    if (!current() || saving.value || !dateValid.value || !windowsValid.value)
      return;
    saving.value = true;
    error.value = '';
    try {
      const saved = await ReservationsService.upsertOverride(
        restaurantId,
        branchId,
        date.value,
        {
          closed: mode.value === 'closed',
          // A closed day takes no bookings at all; the online switch is hidden.
          onlineClosed: mode.value !== 'closed' && onlineClosed.value,
          windows:
            mode.value === 'custom'
              ? windows.value.map((window) => ({
                  from: window.from,
                  to: window.to,
                  maxCovers:
                    window.maxCovers === '' ? null : Number(window.maxCovers),
                  arrivalTimes: window.arrivalTimes,
                }))
              : null,
          slotIntervalMin:
            mode.value === 'custom' ? slotIntervalMin.value : null,
          note: note.value.trim() || null,
          ...reservationDepositScopePatch(
            mode.value === 'closed' ? null : depositRequired.value,
            props.override?.date === date.value ? props.override : undefined
          ),
        }
      );
      if (!current()) return;
      ui.showToast(AlertType.Success, tr('reservations.dates.saved'));
      proxy.$parent?.$emit('saved', saved);
      modals.hide(modalName);
    } catch (cause) {
      if (!current()) return;
      saving.value = false;
      error.value = tr('reservations.dates.error');
      ui.serverError(cause, error.value);
    }
  }
</script>

<template>
  <form
    ref="form"
    class="reservation-date-modal"
    novalidate
    @submit.prevent="save"
  >
    <header class="modal-header">
      <h2>{{ $t('reservations.dates.form.title') }}</h2>
    </header>
    <div class="modal-content">
      <PosUiField
        :label="$t('reservations.dates.form.date')"
        input-id="reservation-override-date"
        :error="
          submitted && !dateValid
            ? $t('reservations.dates.form.dateRequired')
            : undefined
        "
        required
        ><input
          id="reservation-override-date"
          v-model="date"
          type="date"
          class="native-input"
          required
          :disabled="saving || !!override"
      /></PosUiField>
      <PosUiSegmented
        :value="mode"
        :items="modeItems"
        :aria-label="$t('reservations.dates.form.windows')"
        block
        @input="setMode"
      />
      <div v-show="mode === 'custom'" class="windows">
        <div
          v-for="(window, index) in windows"
          :key="window.id"
          class="window-card"
        >
          <div class="window-times">
            <PosUiField
              :label="$t('reservations.shifts.form.from')"
              :input-id="`override-from-${window.id}`"
              ><input
                :id="`override-from-${window.id}`"
                v-model="window.from"
                class="native-input"
                type="time"
                step="900"
                :disabled="saving || mode !== 'custom'" /></PosUiField
            ><PosUiField
              :label="$t('reservations.shifts.form.to')"
              :input-id="`override-to-${window.id}`"
              ><input
                :id="`override-to-${window.id}`"
                v-model="window.to"
                class="native-input"
                type="time"
                step="900"
                :disabled="saving || mode !== 'custom'" /></PosUiField
            ><PosUiButton
              class="remove-window"
              color="error"
              variant="ghost"
              icon="delete-outline"
              :aria-label="$t('delete')"
              :disabled="saving"
              @click="windows.splice(index, 1)"
            />
          </div>
          <p v-if="window.to < window.from" class="hint">
            {{ $t('reservations.shifts.crossesMidnight') }}
          </p>
          <PosUiField
            :label="$t('reservations.shifts.form.maxCovers')"
            :input-id="`override-covers-${window.id}`"
            ><PosUiInput
              :id="`override-covers-${window.id}`"
              v-model="window.maxCovers"
              type="number"
              min="1"
              max="32767"
              step="1"
              inputmode="numeric"
              :disabled="saving || mode !== 'custom'"
          /></PosUiField>
        </div>
        <p v-if="!windowsValid" class="warning" role="alert">
          {{ $t('reservations.dates.form.windowsInvalid') }}
        </p>
        <PosUiButton
          color="neutral"
          variant="outline"
          icon="plus"
          :disabled="saving || windows.length >= 24"
          @click="addWindow"
          >{{ $t('reservations.dates.form.addWindow') }}</PosUiButton
        >
        <PosUiField
          :label="$t('reservations.shifts.form.interval')"
          input-id="reservation-override-interval"
          ><select
            id="reservation-override-interval"
            class="native-input"
            :value="slotIntervalMin ?? ''"
            :disabled="saving || mode !== 'custom'"
            @change="intervalChanged"
          >
            <option value="">
              {{ $t('reservations.dates.form.intervalInherit') }}
            </option>
            <option
              v-for="minutes in intervals"
              :key="minutes"
              :value="minutes"
            >
              {{ $t('reservations.common.minutes', { minutes }) }}
            </option>
          </select></PosUiField
        >
      </div>
      <V2ReservationDepositScopeField
        v-if="configuration.settings.depositsAvailable && mode !== 'closed'"
        v-model="depositRequired"
        :label="$t('reservations.dates.form.deposit')"
        :settings="configuration.settings"
        :disabled="saving"
      />
      <label v-if="mode !== 'closed'" class="switch-row"
        ><span>{{ $t('reservations.dates.form.onlineClosed') }}</span
        ><PosUiSwitch
          v-model="onlineClosed"
          :disabled="saving"
          :aria-label="$t('reservations.dates.form.onlineClosed')"
      /></label>
      <PosUiField
        :label="$t('reservations.dates.form.note')"
        input-id="reservation-override-note"
        ><PosUiTextarea
          id="reservation-override-note"
          v-model="note"
          :maxlength="255"
          :rows="2"
          :disabled="saving"
      /></PosUiField>
      <p v-if="error" class="error" role="alert">{{ error }}</p>
    </div>
    <footer class="modal-actions">
      <PosUiButton
        color="neutral"
        variant="outline"
        :disabled="saving"
        @click="modals.hide(modalName)"
        >{{ $t('cancel') }}</PosUiButton
      ><PosUiButton type="submit" :disabled="saving">{{
        $t('save')
      }}</PosUiButton>
    </footer>
  </form>
</template>

<style scoped lang="scss">
  .reservation-date-modal {
    @apply tw-flex tw-min-h-0 tw-flex-col tw-bg-white;
    max-height: 90dvh;
    .modal-header {
      @apply tw-border-b tw-border-neutral-200 tw-px-5 tw-py-4;
      h2 {
        @apply tw-m-0 tw-text-lg tw-font-semibold tw-text-neutral-900;
      }
    }
    .modal-content {
      @apply tw-flex tw-min-h-0 tw-flex-col tw-gap-4 tw-overflow-y-auto tw-p-5;
    }
    .modal-actions {
      @apply tw-flex tw-flex-none tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-200 tw-p-4;
    }
    .native-input {
      @apply tw-min-h-11 tw-min-w-0 tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-sm tw-text-neutral-900;
    }
    .windows {
      @apply tw-flex tw-flex-col tw-gap-3;
    }
    .window-card {
      @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-lg tw-bg-canvas tw-p-3;
    }
    .window-times {
      @apply tw-grid tw-items-end tw-gap-2;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 44px;
    }
    .remove-window {
      @apply tw-w-11 tw-px-0;
    }
    .switch-row {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-sm tw-text-neutral-800;
    }
    .hint {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .error {
      @apply tw-m-0 tw-text-sm tw-text-red-600;
    }
    .warning {
      @apply tw-m-0 tw-rounded-lg tw-bg-amber-50 tw-p-3 tw-text-sm tw-text-amber-800;
    }
    :deep(.pos-ui-segmented-option) {
      @apply tw-min-h-11 tw-h-auto tw-whitespace-normal tw-px-2 tw-py-2;
    }
  }
</style>
